import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';
/**
 * ProfileStateContext
 *
 * Tracks which onboarding steps the user has actually completed (vs skipped).
 * Persisted to localStorage so the PreEventHub checklist reflects reality
 * across reloads. All flags default to false for a new user.
 *
 * Setters are called from App.tsx onContinue wrappers — skipping an onboarding
 * step leaves its flag at false, which shows it as pending in the hub checklist.
 */
interface ProjectDraft {
  title: string;
  description: string;
  topicIds: string[];
}
interface ProfileState {
  hasCompletedRole: boolean;
  hasCompletedThemes: boolean;
  hasCompletedProject: boolean;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
  roleId: string;
  roleOther: string;
  orgName: string;
  sectorId: string;
  sectorOther: string;
  selectedThemeIds: string[];
  project: ProjectDraft | null;
}
interface ProfileStateContextType extends ProfileState {
  markRoleDone: () => void;
  markThemesDone: () => void;
  markProjectDone: () => void;
  updateDraft: (updates: Partial<ProfileState>) => void;
  resetAll: () => void;
}
const STORAGE_KEY = 'eiis.profileState';
const DEFAULT_STATE: ProfileState = {
  hasCompletedRole: false,
  hasCompletedThemes: false,
  hasCompletedProject: false,
  firstName: '',
  lastName: '',
  avatarUrl: null,
  roleId: '',
  roleOther: '',
  orgName: '',
  sectorId: '',
  sectorOther: '',
  selectedThemeIds: [],
  project: null
};
const ProfileStateContext = createContext<ProfileStateContextType | undefined>(undefined);
function readFromStorage(): ProfileState {
  if (typeof window === 'undefined') return DEFAULT_STATE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw) as Partial<ProfileState>;
    return {
      ...DEFAULT_STATE,
      ...parsed,
      hasCompletedRole: !!parsed.hasCompletedRole,
      hasCompletedThemes: !!parsed.hasCompletedThemes,
      hasCompletedProject: !!parsed.hasCompletedProject
    };
  } catch {
    return DEFAULT_STATE;
  }
}
function writeToStorage(state: ProfileState) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // fail silently
  }
}
export function ProfileStateProvider({
  children
}: {
  children: ReactNode;
}) {
  const [state, setState] = useState<ProfileState>(() => readFromStorage());
  useEffect(() => {
    writeToStorage(state);
  }, [state]);
  const markRoleDone = useCallback(() => {
    setState(prev => prev.hasCompletedRole ? prev : {
      ...prev,
      hasCompletedRole: true
    });
  }, []);
  const markThemesDone = useCallback(() => {
    setState(prev => prev.hasCompletedThemes ? prev : {
      ...prev,
      hasCompletedThemes: true
    });
  }, []);
  const markProjectDone = useCallback(() => {
    setState(prev => prev.hasCompletedProject ? prev : {
      ...prev,
      hasCompletedProject: true
    });
  }, []);
  const updateDraft = useCallback((updates: Partial<ProfileState>) => {
    setState(prev => ({
      ...prev,
      ...updates
    }));
  }, []);
  const resetAll = useCallback(() => {
    setState(DEFAULT_STATE);
  }, []);
  return <ProfileStateContext.Provider value={{
    ...state,
    markRoleDone,
    markThemesDone,
    markProjectDone,
    updateDraft,
    resetAll
  }} data-id="element-218">
      {children}
    </ProfileStateContext.Provider>;
}
export function useProfileState() {
  const ctx = useContext(ProfileStateContext);
  if (!ctx) throw new Error('useProfileState must be used within ProfileStateProvider');
  return ctx;
}